import { useState, useEffect } from "react";
import {
  supabase,
  DatabaseMosque,
  MosqueInsert,
  MosqueUpdate,
  MosqueSuggestionInsert,
} from "../lib/supabase";
import { Mosque, MosqueFormData } from "../types/mosque";

export const useMosques = () => {
  const [mosques, setMosques] = useState<Mosque[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Normalize legacy type values from database
  const normalizeType = (type: string): 'mosque' | 'prayer_room' | 'hired_hall' => {
    const typeMap: Record<string, 'mosque' | 'prayer_room' | 'hired_hall'> = {
      cami: 'mosque',
      mescit: 'prayer_room',
      'cuma-only': 'hired_hall',
      mosque: 'mosque',
      prayer_room: 'prayer_room',
      hired_hall: 'hired_hall',
    };
    return typeMap[type] || 'mosque';
  };

  const toLegacyType = (type: 'mosque' | 'prayer_room' | 'hired_hall') => {
    const legacyTypeMap: Record<'mosque' | 'prayer_room' | 'hired_hall', 'cami' | 'mescit' | 'cuma-only'> = {
      mosque: 'cami',
      prayer_room: 'mescit',
      hired_hall: 'cuma-only',
    };
    return legacyTypeMap[type];
  };

  const shouldRetryWithLegacyType = (status: number, errorText: string) => {
    if (status !== 400) return false;
    const normalizedError = errorText.toLowerCase();
    return (
      normalizedError.includes('check constraint') &&
      (normalizedError.includes('type') ||
        normalizedError.includes('mosques_type_check') ||
        normalizedError.includes('suggested_type'))
    );
  };

  const shouldRetryWithoutCoordinateFields = (status: number, errorText: string) => {
    if (status !== 400) return false;
    const normalizedError = errorText.toLowerCase();
    return (
      (normalizedError.includes('column') || normalizedError.includes('schema cache')) &&
      (normalizedError.includes('suggested_latitude') || normalizedError.includes('suggested_longitude'))
    );
  };

  const shouldRetryWithoutSuggestionSubmitterName = (status: number, errorText: string) => {
    if (status !== 400) return false;
    const normalizedError = errorText.toLowerCase();
    return (
      normalizedError.includes('submitted_by_name') &&
      (normalizedError.includes('does not exist') ||
        normalizedError.includes('could not find the') ||
        normalizedError.includes('column'))
    );
  };

  const shouldRetryWithoutSubmitterName = (status: number, errorText: string) => {
    if (status !== 400) return false;
    const normalizedError = errorText.toLowerCase();
    return (
      normalizedError.includes('created_by_name') &&
      (normalizedError.includes('does not exist') ||
        normalizedError.includes('could not find the') ||
        normalizedError.includes('column'))
    );
  };

  const shouldRetryWithoutSourceLink = (status: number, errorText: string) => {
    if (status !== 400) return false;
    const normalizedError = errorText.toLowerCase();
    return (
      normalizedError.includes('source_link') &&
      (normalizedError.includes('does not exist') ||
        normalizedError.includes('could not find the') ||
        normalizedError.includes('column'))
    );
  };

  // Convert database mosque to frontend mosque type
  const convertDatabaseMosque = (dbMosque: DatabaseMosque): Mosque => ({
    id: dbMosque.id,
    name: dbMosque.name,
    type: normalizeType(dbMosque.type),
    isPublic: dbMosque.is_public,
    location: {
      lat: dbMosque.latitude,
      lng: dbMosque.longitude,
      address: dbMosque.address,
    },
    facilities: {
      menWc: dbMosque.men_wc,
      womenWc: dbMosque.women_wc,
      menWudu: dbMosque.men_wudu,
      womenWudu: dbMosque.women_wudu,
      womenPrayerArea: dbMosque.women_prayer_area,
    },
    description: dbMosque.description,
    sourceLink: dbMosque.source_link,
    createdBy: dbMosque.created_by_name?.trim() || dbMosque.created_by || "Anonymous",
    createdAt: new Date(dbMosque.created_at),
    updatedAt: new Date(dbMosque.updated_at),
  });

  // Convert frontend mosque to database insert type
  const convertToMosqueInsert = (
    mosque: Omit<Mosque, "id" | "createdBy" | "createdAt" | "updatedAt">,
    createdByUserId?: string,
    submitterName?: string,
  ): MosqueInsert => ({
    name: mosque.name,
    type: mosque.type,
    is_public: mosque.isPublic,
    latitude: mosque.location.lat,
    longitude: mosque.location.lng,
    address: mosque.location.address,
    description: mosque.description,
    source_link: mosque.sourceLink?.trim() || undefined,
    men_wc: mosque.facilities.menWc,
    women_wc: mosque.facilities.womenWc,
    men_wudu: mosque.facilities.menWudu,
    women_wudu: mosque.facilities.womenWudu,
    women_prayer_area: mosque.facilities.womenPrayerArea,
    created_by: createdByUserId,
    created_by_name: submitterName?.trim() || undefined,
    status: "pending", // New mosques start as pending
  });

  const getInsertAuthContext = async (supabaseKey: string) => {
    let accessToken = supabaseKey;
    let userId: string | undefined;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        accessToken = session.access_token;
        userId = session.user?.id;
        return { accessToken, userId };
      }
    } catch (error) {
      console.warn('Unable to get current auth session before mosque insert:', error);
    }

    // If anonymous auth is enabled, ensure inserts run under an actual user JWT
    // so stricter RLS policies using auth.uid() still pass.
    try {
      const { data, error } = await supabase.auth.signInAnonymously();
      if (!error && data.session?.access_token) {
        accessToken = data.session.access_token;
        userId = data.session.user?.id;
      }
    } catch (error) {
      console.warn('Anonymous sign-in not available for mosque insert fallback:', error);
    }

    return { accessToken, userId };
  };

  // Fetch all mosques using direct REST API with pagination (Supabase limits to 1000 per request)
  const fetchMosques = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseKey) {
        throw new Error("Supabase not configured");
      }

      const allMosques: DatabaseMosque[] = [];
      const pageSize = 1000;
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        const response = await fetch(
          `${supabaseUrl}/rest/v1/mosques?status=eq.approved&select=*&limit=${pageSize}&offset=${offset}`,
          {
            headers: {
              apikey: supabaseKey,
              Authorization: `Bearer ${supabaseKey}`,
            },
            signal: controller.signal,
          }
        );

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        allMosques.push(...data);

        if (data.length < pageSize) {
          hasMore = false;
        } else {
          offset += pageSize;
        }
      }

      const convertedMosques = allMosques.map(convertDatabaseMosque);
      setMosques(convertedMosques);
    } catch (err) {
      console.error(">>> Error:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch mosques");
    } finally {
      setIsLoading(false);
    }
  };

  // Add a new mosque (uses REST API to support anonymous users)
  const addMosque = async (
    mosqueData: Omit<Mosque, "id" | "createdBy" | "createdAt" | "updatedAt">,
    submitterName?: string,
  ): Promise<string> => {
    try {
      setError(null);
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseKey) {
        throw new Error("Supabase not configured");
      }

      const authContext = await getInsertAuthContext(supabaseKey);
      const insertData = convertToMosqueInsert(
        mosqueData,
        authContext.userId,
        submitterName,
      );
      const clientGeneratedId = crypto.randomUUID();

      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: supabaseKey,
          Authorization: `Bearer ${authContext.accessToken}`,
          // `return=minimal` avoids a post-insert SELECT, which anon users
          // cannot do for pending rows under current RLS.
          Prefer: 'return=minimal',
        },
      };

      const insertWithPayload = async (payload: Record<string, unknown>) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);
        const response = await fetch(`${supabaseUrl}/rest/v1/mosques`, {
          ...requestOptions,
          signal: controller.signal,
          body: JSON.stringify(payload),
        });
        clearTimeout(timeoutId);
        return response;
      };

      let activePayload: Record<string, unknown> = {
        id: clientGeneratedId,
        ...(insertData as unknown as Record<string, unknown>),
      };
      let response = await insertWithPayload(activePayload);

      if (!response.ok) {
        let errorText = await response.text();

        // Backward compatibility for databases still enforcing legacy type values.
        if (shouldRetryWithLegacyType(response.status, errorText)) {
          activePayload = {
            ...activePayload,
            type: toLegacyType(insertData.type),
          };

          response = await insertWithPayload(activePayload);

          if (!response.ok) {
            errorText = await response.text();
          }
        }

        // Backward compatibility for databases that do not have created_by_name yet.
        if (
          !response.ok &&
          shouldRetryWithoutSubmitterName(response.status, errorText) &&
          Object.prototype.hasOwnProperty.call(activePayload, 'created_by_name')
        ) {
          const { created_by_name, ...payloadWithoutSubmitterName } = activePayload;
          void created_by_name;
          activePayload = payloadWithoutSubmitterName;
          response = await insertWithPayload(activePayload);

          if (!response.ok) {
            errorText = await response.text();
          }
        }

        // Backward compatibility for databases that do not have source_link yet.
        if (
          !response.ok &&
          shouldRetryWithoutSourceLink(response.status, errorText) &&
          Object.prototype.hasOwnProperty.call(activePayload, 'source_link')
        ) {
          const { source_link, ...payloadWithoutSourceLink } = activePayload;
          void source_link;
          activePayload = payloadWithoutSourceLink;
          response = await insertWithPayload(activePayload);

          if (!response.ok) {
            errorText = await response.text();
          }
        }

        if (!response.ok) {
          console.error("Insert error:", response.status, errorText);
          if (response.status === 401 && errorText.includes('42501')) {
            throw new Error('Mosque submission is currently blocked by database policy. Please ask the admin to apply the latest migrations.');
          }
          throw new Error(`Failed to add mosque`);
        }
      }

      return clientGeneratedId;
    } catch (err) {
      console.error("Error adding mosque:", err);
      setError(err instanceof Error ? err.message : "Failed to add mosque");
      throw err;
    }
  };

  // Update a mosque
  const updateMosque = async (
    id: string,
    updates: Partial<
      Omit<Mosque, "id" | "createdBy" | "createdAt" | "updatedAt">
    >,
  ) => {
    try {
      setError(null);

      // Convert frontend updates to database format
      const dbUpdates: MosqueUpdate = {};
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.type !== undefined) dbUpdates.type = updates.type;
      if (updates.isPublic !== undefined)
        dbUpdates.is_public = updates.isPublic;
      if (updates.location?.lat !== undefined)
        dbUpdates.latitude = updates.location.lat;
      if (updates.location?.lng !== undefined)
        dbUpdates.longitude = updates.location.lng;
      if (updates.location?.address !== undefined)
        dbUpdates.address = updates.location.address;
      if (updates.sourceLink !== undefined)
        dbUpdates.source_link = updates.sourceLink?.trim() || undefined;
      if (updates.facilities?.menWc !== undefined)
        dbUpdates.men_wc = updates.facilities.menWc;
      if (updates.facilities?.womenWc !== undefined)
        dbUpdates.women_wc = updates.facilities.womenWc;
      if (updates.facilities?.menWudu !== undefined)
        dbUpdates.men_wudu = updates.facilities.menWudu;
      if (updates.facilities?.womenWudu !== undefined)
        dbUpdates.women_wudu = updates.facilities.womenWudu;
      if (updates.facilities?.womenPrayerArea !== undefined)
        dbUpdates.women_prayer_area = updates.facilities.womenPrayerArea;

      const { data, error: updateError } = await supabase
        .from("mosques")
        .update(dbUpdates)
        .eq("id", id)
        .select()
        .single();

      if (updateError) {
        throw updateError;
      }

      const updatedMosque = convertDatabaseMosque(data);
      setMosques((prev) =>
        prev.map((mosque) => (mosque.id === id ? updatedMosque : mosque)),
      );
      return updatedMosque;
    } catch (err) {
      console.error("Error updating mosque:", err);
      setError(err instanceof Error ? err.message : "Failed to update mosque");
      throw err;
    }
  };

  // Delete a mosque
  const deleteMosque = async (id: string) => {
    try {
      setError(null);

      const { error: deleteError } = await supabase
        .from("mosques")
        .delete()
        .eq("id", id);

      if (deleteError) {
        throw deleteError;
      }

      setMosques((prev) => prev.filter((mosque) => mosque.id !== id));
    } catch (err) {
      console.error("Error deleting mosque:", err);
      setError(err instanceof Error ? err.message : "Failed to delete mosque");
      throw err;
    }
  };

  // Find nearby mosques
  const findNearbyMosques = async (
    lat: number,
    lng: number,
    radiusKm: number = 10,
  ) => {
    try {
      setError(null);

      const { data, error: nearbyError } = await supabase.rpc(
        "find_nearby_mosques",
        {
          user_lat: lat,
          user_lon: lng,
          radius_km: radiusKm,
        },
      );

      if (nearbyError) {
        throw nearbyError;
      }

      return (
        data?.map((mosque: any) => ({
          ...convertDatabaseMosque(mosque),
          distance: mosque.distance_km,
        })) || []
      );
    } catch (err) {
      console.error("Error finding nearby mosques:", err);
      setError(
        err instanceof Error ? err.message : "Failed to find nearby mosques",
      );
      return [];
    }
  };

  // Submit a suggestion for editing a mosque
  const submitSuggestion = async (
    mosqueId: string,
    changes: Partial<MosqueFormData>,
    reason: string,
    submitterName?: string
  ) => {
    try {
      setError(null);

      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseKey) {
        throw new Error("Supabase not configured");
      }

      const suggestionData: MosqueSuggestionInsert = {
        mosque_id: mosqueId,
        suggested_name: changes.name,
        suggested_type: changes.type,
        suggested_is_public: changes.isPublic,
        suggested_latitude: changes.location?.lat,
        suggested_longitude: changes.location?.lng,
        suggested_description: changes.description,
        suggested_men_wc: changes.facilities?.menWc,
        suggested_women_wc: changes.facilities?.womenWc,
        suggested_men_wudu: changes.facilities?.menWudu,
        suggested_women_wudu: changes.facilities?.womenWudu,
        suggested_women_prayer_area: changes.facilities?.womenPrayerArea,
        submitted_by_name: submitterName?.trim() || undefined,
        reason: reason,
        status: 'pending',
      };

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const requestOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: supabaseKey,
          Authorization: `Bearer ${supabaseKey}`,
          Prefer: 'return=minimal',
        },
        signal: controller.signal,
      };

      let response = await fetch(`${supabaseUrl}/rest/v1/mosque_edit_suggestions`, {
        ...requestOptions,
        body: JSON.stringify(suggestionData),
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorText = await response.text();

        // Backward compatibility for databases still enforcing legacy suggested_type values.
        if (suggestionData.suggested_type && shouldRetryWithLegacyType(response.status, errorText)) {
          const legacySuggestionData = {
            ...suggestionData,
            suggested_type: toLegacyType(suggestionData.suggested_type),
          };

          response = await fetch(`${supabaseUrl}/rest/v1/mosque_edit_suggestions`, {
            ...requestOptions,
            body: JSON.stringify(legacySuggestionData),
          });

          if (!response.ok) {
            errorText = await response.text();
          }
        }

        if (!response.ok && shouldRetryWithoutCoordinateFields(response.status, errorText)) {
          const { suggested_latitude, suggested_longitude, ...legacySchemaPayload } = suggestionData;

          response = await fetch(`${supabaseUrl}/rest/v1/mosque_edit_suggestions`, {
            ...requestOptions,
            body: JSON.stringify(legacySchemaPayload),
          });

          if (!response.ok) {
            errorText = await response.text();
          }
        }

        if (
          !response.ok &&
          shouldRetryWithoutSuggestionSubmitterName(response.status, errorText) &&
          Object.prototype.hasOwnProperty.call(suggestionData, 'submitted_by_name')
        ) {
          const { submitted_by_name, ...payloadWithoutSubmitterName } = suggestionData;
          void submitted_by_name;

          response = await fetch(`${supabaseUrl}/rest/v1/mosque_edit_suggestions`, {
            ...requestOptions,
            body: JSON.stringify(payloadWithoutSubmitterName),
          });

          if (!response.ok) {
            errorText = await response.text();
          }
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
      }

    } catch (err) {
      console.error("Error submitting suggestion:", err);
      setError(err instanceof Error ? err.message : "Failed to submit suggestion");
      throw err;
    }
  };

  // Fetch mosques on component mount
  useEffect(() => {
    fetchMosques();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  return {
    mosques,
    isLoading,
    error,
    addMosque,
    updateMosque,
    deleteMosque,
    findNearbyMosques,
    submitSuggestion,
    refetch: fetchMosques,
  };
};
